import type { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IApiGetAppUserResponseBody } from '~/lib/api/modules/Apps';
import type { IUseUpdateAppUserMutationValues } from '~/lib/api/modules/Apps/hooks/useUpdateAppUserMutation';
import type { IAppUserFormProps, IAppUserFormValues } from './AppUserForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useAppUserQuery } from '~/lib/api/modules/Apps/hooks/useAppUserQuery';
import { useUpdateAppUserMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppUserMutation';
import { AppUserForm } from './AppUserForm';

export type IUpdateAppUserFormProps = Omit<
  IAppUserFormProps,
  'mode' | 'organizationId'
> & {
  onSuccess?: (
    outputData: IApiGetAppUserResponseBody,
    inputValues: IUseUpdateAppUserMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
  userId: string;
};

export const UpdateAppUserForm: React.FC<IUpdateAppUserFormProps> = (props) => {
  const { sx, onSuccess, cancelUrl, appId, userId } = props;
  const { t } = useTranslation();
  const appUserQuery = useAppUserQuery({
    appId,
    userId,
  });
  const appQuery = useAppQuery({ appId });
  const appUpdateUserMutation = useUpdateAppUserMutation();
  const appUser = appUserQuery.data;

  const handleSubmit: SubmitHandler<IAppUserFormValues> = (values) => {
    if (appUpdateUserMutation.isLoading) {
      return;
    }

    return appUpdateUserMutation.mutateAsync(
      {
        appId,
        userId,
        body: {
          roles: values.roles ?? ['viewer'],
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Title>{t('UpdateAppUserForm.title')}</ContentBlock.Title>
      <ContentBlock.Suspense
        loading={appQuery.isLoading || appUserQuery.isLoading}
        error={appQuery.error ?? appUserQuery.error}
      >
        {appUser && appQuery.data?.organization?._id ? (
          <AppUserForm
            mode="edition"
            organizationId={appQuery.data.organization._id}
            cancelUrl={cancelUrl}
            defaultValues={{
              userId: appUser._id,
              roles: appUser.roles,
            }}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
